import { SurveyObservationContext, ObservedSurveyRender, observerFor, surveyObservationScope } from '../../flow/presentation/SurveyObservationContext';
import { CanonicalActionUnavailableError } from '../../flow/presentation/canonicalActionError';
import CanonicalSurveyRenderer from "../../flow/presentation/CanonicalSurveyRenderer";
import { selectSurveyPresentation } from "../../flow/presentation/surveyPresentation";
import { canonicalExecutionPolicy, canonicalSurveyContext } from "../../flow/presentation/canonicalSurveyContext";
import { canonicalSurveyToFlow } from "../../flow/presentation/canonicalSurveyFlow";
import { createCanonicalFlowDriver } from "../../flow/presentation/canonicalFlowDriver";
import { FlowRuntimeProvider } from "../../flow/FlowRuntimeContext";
import { refreshedRuntimeContext } from "../../services/cxRuntimeContext";
import React, { useEffect, useMemo, useRef, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import FlowRenderer from "../../flow/FlowRenderer";
import { publicSurveyToFlow } from "../../flow/customerSurveyFlow";
import {
  getPublicCXSurvey,
  submitPublicCXSurvey,
  trackPublicCXSurveyLinkOpen,
  trackPublicCXSurveyReviewClick,
  trackPublicCXSurveyReviewCTAView,
  type CXPublicSurveySubmission,
  type CXPublicRedirectTarget,
  type CXPublicSurveyResponse,
} from "../../services/cxPublicSurveyService";
import type { FlowActionEvent } from "../../flow/types";
import type { FlowConfig, FlowValues } from "../../flow/types";

interface PublicCXSurveyWrapperProps {
  surveyCode: string;
}

export const TEST_MODE_NOTICE_DURATION_MS = 6_000;

const PublicCXSurveySession: React.FC<PublicCXSurveyWrapperProps> = ({
  surveyCode,
}) => {
  const [survey, setSurvey] = useState<CXPublicSurveyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submissionState, setSubmissionState] = useState<
    "idle" | "submitting" | "submitted" | "completed_without_response" | "error"
  >("idle");
  const [redirectAttempt, setRedirectAttempt] = useState(0);
  const actionPending = useRef(false);
  const refreshPending = useRef(false);
  const [mediaRevision, setMediaRevision] = useState(0);
  const [refreshingMedia, setRefreshingMedia] = useState(false);
  const [mediaRefreshError, setMediaRefreshError] = useState<string | null>(null);
  const prepared = useMemo(() => {
    if (!survey) return null;
    try { return { ...prepareSurvey(survey), error: null }; }
    catch { return { flow: null, presentation: null, driver: null, error: "This survey configuration is invalid or is not supported yet." }; }
  }, [survey]);
  const flow = prepared?.flow;
  const canonical = prepared?.presentation?.kind === "feedback_2026" ? prepared.presentation.compiled : null;
  const observe = useMemo(() => survey && flow && !prepared?.error
    ? observerFor(surveyObservationScope(survey, canonical))
    : () => {}, [survey, flow, prepared?.error, canonical]);
  const [testModeNoticeVisible, setTestModeNoticeVisible] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const assertActive = () => {
    if (!alive.current) throw new Error("Survey session has ended");
  };
  const lastSavedSubmission = useRef("");
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const redirectUrl =
    survey && !survey.testMode && !prepared?.error ? publicCXSurveyRedirectUrl(survey) : null;

  useEffect(() => {
    let active = true;

    async function fetchSurvey() {
      try {
        setLoading(true);
        setError(null);
        const response = await getPublicCXSurvey(surveyCode);
        if (!active) return;
        prepareSurvey(response);
        if (!response.testMode) {
          try {
            await trackPublicCXSurveyLinkOpen(surveyCode);
          } catch (trackError) {
            console.warn("Failed to track CX survey link open:", trackError);
          }
        }
        if (active) setSurvey(response);
      } catch (fetchError) {
        console.error("Failed to load CX survey experience:", fetchError);
        if (active) setError("This survey link could not be loaded.");
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchSurvey();

    return () => {
      active = false;
    };
  }, [surveyCode]);

  useEffect(() => {
    if (!survey?.redirect || !redirectUrl) return;

    let active = true;

    async function openRedirect() {
      setError(null);
      try {
        await trackPublicCXSurveyReviewClick(
          surveyCode,
          survey.redirect.destinationType === "google_location" ? "google" : undefined,
        );
      } catch (trackError) {
        console.warn(
          "Failed to track CX review destination click:",
          trackError,
        );
        if (active) setError("We could not open this link. Please try again.");
        return;
      }

      if (active) window.location.replace(redirectUrl);
    }

    void openRedirect();

    return () => {
      active = false;
    };
  }, [redirectUrl, survey, surveyCode, redirectAttempt]);

  useEffect(() => {
    if (!survey?.testMode) {
      setTestModeNoticeVisible(false);
      return;
    }

    setTestModeNoticeVisible(true);
    const timeout = window.setTimeout(
      () => setTestModeNoticeVisible(false),
      TEST_MODE_NOTICE_DURATION_MS,
    );

    return () => window.clearTimeout(timeout);
  }, [survey?.testMode, surveyCode]);

  const refreshMedia = async () => {
    if (refreshPending.current || !survey?.runtimeContext) return;
    refreshPending.current = true;
    setRefreshingMedia(true);
    setMediaRefreshError(null);
    const started = performance.now();
    try {
      const fresh = await getPublicCXSurvey(surveyCode);
      const runtimeContext = refreshedRuntimeContext(survey, fresh);
      if (alive.current) {
        setSurvey((current) => current ? { ...current, runtimeContext } : current);
        setMediaRevision((revision) => revision + 1);
        observe({ category: 'media', detail: 'refresh', outcome: 'success', durationMs: performance.now() - started });
      }
    } catch {
      if (alive.current) observe({ category: 'media', detail: 'refresh', outcome: 'failure', durationMs: performance.now() - started });
      if (alive.current) setMediaRefreshError("Media could not be refreshed. Your answers are still here. Try again or reopen your invitation.");
    } finally {
      refreshPending.current = false;
      if (alive.current) setRefreshingMedia(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "#F8FAFC",
          display: "flex",
          minHeight: "100vh",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CircularProgress size={34} />
          <Typography
            sx={{ color: "#475569", fontWeight: 700 }}
            variant="body2"
          >
            Loading survey
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error || prepared?.error || !survey || !flow) {
    return (
      <Box sx={{ bgcolor: "#F8FAFC", minHeight: "100vh", p: 3 }}>
        <Alert severity="error">
          {error ?? prepared?.error ?? "This survey link could not be loaded."}
        </Alert>
        {survey && redirectUrl ? <Button onClick={() => setRedirectAttempt((attempt) => attempt + 1)}>Try again</Button> : null}
      </Box>
    );
  }

  if (survey.redirect && !redirectUrl && !(canonical && survey.testMode)) {
    return (
      <Box sx={{ bgcolor: "#F8FAFC", minHeight: "100vh", p: 3 }}>
        <Alert severity="error">This redirect link is not configured.</Alert>
      </Box>
    );
  }

  if (redirectUrl) {
    return (
      <Box
        sx={{
          alignItems: "center",
          bgcolor: "#F8FAFC",
          display: "flex",
          minHeight: "100vh",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CircularProgress size={34} />
          <Typography
            sx={{ color: "#475569", fontWeight: 700 }}
            variant="body2"
          >
            Opening link
          </Typography>
        </Stack>
      </Box>
    );
  }

  const initialValues = publicSurveyInitialValues(flow, survey, surveyCode, canonical ? { componentId: prepared?.driver?.initialRatingComponentId } : undefined);
  const stripeReturnSessionId = new URL(window.location.href).searchParams.get(
    "stripe_tip_session_id",
  );
  const stripeReturnScreenId = stripeReturnSessionId
    ? flow.screens.find((screen) =>
        screen.components.some(
          (component) =>
            component.type === "tipping" && component.checkoutEnabled,
        ),
      )?.id
    : undefined;

  const saveValues = async (values: FlowValues, showState = false, ratingComponentId?: string) => {
    const answerIds = () => flow.screens.flatMap((screen) => screen.components)
      .filter((component) =>
        (component.id === ratingComponentId || component.type === "feedbackForm") &&
        values[component.id] != null)
      .map((component) => component.id);
    const submission = publicSurveySubmission(flow, values, canonical ? { componentId: ratingComponentId } : undefined);
    if (!submission) {
      // Plain canonical navigation can leave a scoreless draft for a later screen.
      // The API requires a rating; no persistence means no acknowledgement or edit lock.
      if (canonical) return [];
      const hasFeedbackSubmission = flow.screens.some((screen) => screen.components.some(
        (component) => component.type === "feedbackForm" && values[component.id] != null
      ));
      if (hasFeedbackSubmission) throw new Error("A rating is required before feedback can be saved.");
      return;
    }

    if (canonical && !canonicalExecutionPolicy(survey).persist) {
      // A public test invitation acknowledges validated answers only in this
      // mounted session, so the shared summary, media, and Edit UI can behave
      // like a live survey. No API call or durable state is created.
      return survey.testMode === true ? answerIds() : [];
    }

    const serialized = JSON.stringify(submission);
    const persist = async () => {
      assertActive();
      if (serialized === lastSavedSubmission.current) {
        if (showState) setSubmissionState("submitted");
        return;
      }

      if (showState) setSubmissionState("submitting");
      const started = performance.now();
      try {
        await submitPublicCXSurvey(surveyCode, submission, survey.testMode === true);
        assertActive();
        observe({ category: 'submission', detail: 'response', outcome: 'success', durationMs: performance.now() - started });
      } catch (error) {
        if (alive.current) observe({ category: 'submission', detail: 'response', outcome: 'failure', durationMs: performance.now() - started });
        throw error;
      }
      lastSavedSubmission.current = serialized;
      if (showState) setSubmissionState("submitted");
    };

    const queuedSave = saveQueue.current.then(persist, persist);
    saveQueue.current = queuedSave.catch(() => undefined);
    await queuedSave;
    return canonical
      ? answerIds()
      : [];
  };

  const handleCanonicalAction = async (action: FlowActionEvent) => {
    if (!['contact', 'callback', 'referral', 'review'].includes(action.type)) return;
    const detail = action.type as 'review' | 'contact' | 'callback' | 'referral';
    const started = performance.now();
    try {
      const policy = canonicalExecutionPolicy(survey);
      const phone = action.phone?.replace(/[^\d+*#,;]/g, "");
      const url = action.url && action.url !== "#" ? validHttpUrl(action.url) : null;
      if (action.url && action.url !== "#" && !url) throw new Error("Unsupported destination.");
      if (!url && !phone) {
        if (action.type === "callback") throw new CanonicalActionUnavailableError("Callback requests are not available for this survey.");
        if (action.type === "referral") throw new CanonicalActionUnavailableError("A referral form destination has not been configured. No referral was created.");
        if (action.type === "review" || action.type === "contact") throw new CanonicalActionUnavailableError("This action does not have a configured destination.");
        return;
      }
      if (!policy.external) throw new Error("External actions are unavailable in test mode.");
      assertActive();
      if (action.type === "review") {
        const provider = reviewActionProvider(action);
        await (provider
          ? trackPublicCXSurveyReviewClick(surveyCode, provider)
          : trackPublicCXSurveyReviewClick(surveyCode));
      }
      assertActive();
      window.location.assign(url ?? `tel:${phone}`);
      observe({ category: 'cta', detail, outcome: 'success', durationMs: performance.now() - started });
    } catch (error) {
      if (alive.current) observe({ category: 'cta', detail, outcome: error instanceof CanonicalActionUnavailableError ? 'unavailable' : 'failure', durationMs: performance.now() - started });
      throw error;
    }
  };

  const handleAction = (action: FlowActionEvent) => {
    const actionValues =
      action.metadata?.values && typeof action.metadata.values === "object"
        ? (action.metadata.values as FlowValues)
        : null;

    const phoneTarget = action.phone?.replace(/[^\d+*#,;]/g, "") ?? "";
    const actionTarget =
      action.url && action.url !== "#"
        ? action.url
        : phoneTarget
          ? `tel:${phoneTarget}`
          : null;

    if (actionTarget) {
      if (survey.testMode) {
        if (action.type === "review") {
          window.open(actionTarget, "_self");
        }
        return;
      }
      if (actionPending.current) return;
      actionPending.current = true;
      let started: number | undefined;
      const observeAction = (outcome: 'success' | 'failure') => {
        if (alive.current && started !== undefined && ['review', 'contact', 'callback', 'referral'].includes(action.type))
          observe({ category: 'cta', detail: action.type as 'review' | 'contact' | 'callback' | 'referral', outcome, durationMs: performance.now() - started });
      };
      void (async () => {
        try {
          if (actionValues) await saveValues(actionValues);
          assertActive();
          started = performance.now();
          if (action.type === "review") {
            const provider = reviewActionProvider(action);
            await (provider
              ? trackPublicCXSurveyReviewClick(surveyCode, provider)
              : trackPublicCXSurveyReviewClick(surveyCode));
          }
        } catch (submissionError) {
          observeAction('failure');
          console.warn(
            "Failed to save or track the CX survey review destination:",
            submissionError,
          );
          if (alive.current) setSubmissionState("error");
          actionPending.current = false;
          return;
        }
        try {
          if (alive.current) {
            window.location.assign(actionTarget);
            observeAction('success');
          }
        } catch (error) {
          observeAction('failure');
          console.warn("Failed to open the CX survey destination:", error);
        } finally {
          // tel/mailto dispatch may leave this survey mounted.
          actionPending.current = false;
        }
      })();
      return;
    }

    // Forward navigation owns persistence through beforeNavigate.

  };

  const handleComplete = async (values: FlowValues) => {
    const submission = publicSurveySubmission(flow, values);
    if (!submission) {
      setSubmissionState("completed_without_response");
      return;
    }

    setSubmissionState("submitting");
    try {
      await saveValues(values, true);
    } catch (submissionError) {
      console.error("Failed to submit CX survey response:", submissionError);
      if (alive.current) setSubmissionState("error");
    }
  };

  return (
    <>
      {survey.testMode ? (
        <Fade in={testModeNoticeVisible} timeout={240} unmountOnExit>
          <Alert
            severity="info"
            sx={{
              left: "50%",
              maxWidth: 680,
              position: "fixed",
              top: 12,
              transform: "translateX(-50%)",
              width: "calc(100% - 24px)",
              zIndex: 2000,
            }}
          >
            CX Studio test mode — feedback is not saved, tips are disabled, and
            review clicks are not recorded.
          </Alert>
        </Fade>
      ) : null}
      {submissionState === "error" ? <Alert severity="error">We could not save your feedback. Your answers are still here. Please try again.</Alert> : null}
      {mediaRefreshError ? <Alert severity="warning">{mediaRefreshError}</Alert> : null}
      <SurveyObservationContext.Provider value={observe}>
      <FlowRuntimeProvider mode="live" surveyInviteCode={surveyCode} paymentsEnabled={survey.testMode !== true && survey.persistenceDisabled !== true && survey.tipsDisabled !== true && survey.runtimeContext?.capabilities.legacyFinixEnabled === true} mediaRevision={mediaRevision} refreshMedia={survey.runtimeContext ? refreshMedia : undefined} refreshingMedia={refreshingMedia}>
      <ObservedSurveyRender>
      {canonical ? <CanonicalSurveyRenderer key={surveyCode} compiled={canonical} response={survey}
        onReviewCTAVisible={canonicalExecutionPolicy(survey).external ? (provider) => trackPublicCXSurveyReviewCTAView(surveyCode, provider) : undefined}
        initialValues={initialValues} beforeNavigate={async (values, owner) => (await saveValues(values, false, owner)) ?? []} onAction={handleCanonicalAction} /> : <FlowRenderer
        key={surveyCode}
        beforeNavigate={async (values) => { await saveValues(values); }}
        completionMessage={
          submissionState === "completed_without_response"
            ? "This experience is complete."
            : survey.testMode
            ? submissionState === "submitting"
              ? "Testing your survey…"
              : submissionState === "error"
                ? "The test could not be completed. Please go back and try again."
                : "Test complete. Nothing was saved."
            : submissionState === "submitting"
              ? "Saving your feedback…"
              : submissionState === "error"
                ? "We could not save your feedback. Please go back and try again."
                : "Thank you. Your feedback has been submitted."
        }
        flow={flow}
        initialScreenId={stripeReturnScreenId ?? flow.entryScreenId}
        initialValues={initialValues}
        onAction={handleAction}
        onComplete={(values) => void handleComplete(values)}
      />}
      </ObservedSurveyRender>
      </FlowRuntimeProvider>
      </SurveyObservationContext.Provider>
    </>
  );
};

// A link change starts fresh state and queues; old async work cannot affect it.
const PublicCXSurveyWrapper: React.FC<PublicCXSurveyWrapperProps> = (props) => (
  <PublicCXSurveySession key={props.surveyCode} {...props} />
);

export function publicSurveyInitialValues(
  flow: FlowConfig,
  survey: CXPublicSurveyResponse,
  surveyCode: string,
  ratingOwner?: { componentId?: string },
): FlowValues {
  const values: FlowValues = {
    appointmentCode: survey.appointmentCode,
    cxExecutionStepId: survey.executionStepId,
    cxSurveyCode: surveyCode,
    cxTriggerGroupId: survey.triggerGroupId,
  };
  const existing = survey.existingResponse;
  if (!existing) return values;

  const ratingComponent = flow.screens
    .flatMap((screen) => screen.components)
    .find((component) =>
      (!ratingOwner || component.id === ratingOwner.componentId) &&
      (existing.scoreMethod === "NPS" ? component.type === "npsForm" : component.type === "csatForm"),
    );
  if (!ratingComponent) return values;

  values[ratingComponent.id] = {
    componentId: ratingComponent.id,
    feedback: {
      notes: existing.comments ?? "",
      tags: existing.tags ?? [],
    },
    responseId: String(existing.ratingId),
    score: existing.rating,
    submitted: true,
  };
  return values;
}

export function publicSurveySubmission(
  flow: FlowConfig,
  values: FlowValues,
  ratingOwner?: { componentId?: string },
): CXPublicSurveySubmission | null {
  const ratingComponent = flow.screens
    .flatMap((screen) => screen.components)
    .find(
      (component) =>
        (!ratingOwner || component.id === ratingOwner.componentId) &&
        (component.type === "npsForm" || component.type === "csatForm") &&
        ratingScore(values[component.id]) !== null,
    );
  if (
    !ratingComponent ||
    (ratingComponent.type !== "npsForm" && ratingComponent.type !== "csatForm")
  ) {
    return null;
  }

  const rating = ratingScore(values[ratingComponent.id]);
  if (rating === null) return null;

  const comments: string[] = [];
  const tags = new Set<string>();
  for (const value of Object.values(values)) {
    if (!value || typeof value !== "object") continue;
    const record = value as Record<string, unknown>;
    const feedback =
      record.feedback && typeof record.feedback === "object"
        ? (record.feedback as Record<string, unknown>)
        : record;
    if (typeof feedback.notes === "string" && feedback.notes.trim()) {
      comments.push(feedback.notes.trim());
    }
    if (Array.isArray(feedback.tags)) {
      feedback.tags.forEach((tag) => {
        if (typeof tag === "string" && tag.trim()) tags.add(tag.trim());
      });
    }
  }

  return {
    comments: [...new Set(comments)].join("\n\n"),
    rating,
    scoreMethod: ratingComponent.type === "npsForm" ? "NPS" : "CSAT",
    tags: [...tags],
  };
}

function ratingScore(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (!value || typeof value !== "object") return null;
  const score = (value as { score?: unknown }).score;
  return typeof score === "number" && Number.isInteger(score) ? score : null;
}

function reviewActionProvider(
  action: FlowActionEvent,
): "google" | "yelp" | "facebook" | "bbb" | undefined {
  const provider = action.metadata?.provider;
  return provider === "google" ||
    provider === "yelp" ||
    provider === "facebook" ||
    provider === "bbb"
    ? provider
    : undefined;
}

export function publicCXSurveyRedirectUrl(
  survey: CXPublicSurveyResponse,
): string | null {
  const redirect = survey.redirect;
  if (!redirect) return null;

  const topLevelUrl = validHttpUrl(redirectTargetUrl(redirect));
  if (topLevelUrl) return topLevelUrl;

  for (const target of redirect.targets ?? []) {
    const targetUrl = validHttpUrl(redirectTargetUrl(target));
    if (targetUrl) return targetUrl;
  }

  return null;
}

function redirectTargetUrl(
  target: CXPublicRedirectTarget,
): string | null | undefined {
  return (
    target.targetUrl ||
    (target.destinationType === "google_location"
      ? target.googleReviewUrl
      : target.customUrl)
  );
}

function validHttpUrl(value?: string | null): string | null {
  if (!value) return null;

  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:"
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export default PublicCXSurveyWrapper;

function prepareSurvey(survey: CXPublicSurveyResponse) {
  const presentation = selectSurveyPresentation(survey);
  if (presentation.kind === "legacy") return { presentation, flow: publicSurveyToFlow(survey), driver: null };
  const context = canonicalSurveyContext(presentation.compiled, survey);
  const driver = createCanonicalFlowDriver(presentation.compiled, survey);
  return { presentation, driver, flow: canonicalSurveyToFlow(presentation.compiled, survey, context) };
}
